// src/pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { reportService } from '../services/reportService';
import { User, Save, ArrowLeft, ShieldCheck, AlertCircle, BarChart3, Unlock, BadgeCheck, CheckSquare, Square, Loader2, Mail, Phone, RefreshCw } from 'lucide-react';

const MAX_DAILY_POSTS = 3;
const KYC_THRESHOLD = 5;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile: authUserProfile, refreshUserProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Fetched profile from Firestore
  const [firestoreProfile, setFirestoreProfile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  
  // KYC State
  const [showKYCForm, setShowKYCForm] = useState(false);
  const [idNumber, setIdNumber] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Stats
  const [dailyCount, setDailyCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    loadProfile();
  }, [currentUser, navigate]);

  const loadProfile = async (isRefresh = false) => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');

      // Fetch profile from Firestore
      const fetchedProfile = await userService.getUser(currentUser.uid);
      
      let profileToUse;
      
      if (fetchedProfile) {
        // User has a document in Firestore - use it
        profileToUse = fetchedProfile;
        setFirestoreProfile(fetchedProfile);
      } else {
        // No Firestore document - use AuthContext profile as fallback
        // This happens on first login (especially Google sign-in)
        if (authUserProfile) {
          profileToUse = authUserProfile;
          setFirestoreProfile(null);
          
          // Create initial Firestore document with auth data
          await userService.saveUser(currentUser.uid, {
            name: authUserProfile.name || currentUser.displayName || '',
            email: authUserProfile.email || currentUser.email || '',
            mobile: authUserProfile.mobile || '',
            roles: authUserProfile.roles || ['LENDER'],
            isVerified: authUserProfile.isVerified || false,
            createdAt: new Date().toISOString()
          });
          
          // Fetch the newly created profile
          const newProfile = await userService.getUser(currentUser.uid);
          if (newProfile) {
            profileToUse = newProfile;
            setFirestoreProfile(newProfile);
          }
        } else {
          // Shouldn't happen, but handle gracefully
          setError('Unable to load profile data. Please try refreshing.');
          return;
        }
      }

      // Populate form with profile data
      setFormData({
        name: profileToUse.name || '',
        email: profileToUse.email || '',
        mobile: profileToUse.mobile || '',
      });
      setSelectedRoles(profileToUse.roles || ['LENDER']);

      // Load stats if user is a lender
      if (profileToUse.roles?.includes('LENDER')) {
        const [daily, total] = await Promise.all([
          reportService.getDailyPostCount(currentUser.uid),
          reportService.getTotalReportCount(currentUser.uid)
        ]);
        setDailyCount(daily);
        setTotalCount(total);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadProfile(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleRole = (role) => {
    if (selectedRoles.includes(role)) {
      if (selectedRoles.length > 1) {
        setSelectedRoles(selectedRoles.filter(r => r !== role));
      } else {
        setError("You must have at least one active role.");
        setTimeout(() => setError(''), 3000);
      }
    } else {
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      // Save to Firestore
      await userService.saveUser(currentUser.uid, {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        roles: selectedRoles,
      });

      // Refresh both Firestore profile and AuthContext
      await Promise.all([
        loadProfile(true),
        refreshUserProfile()
      ]);

      setMessage('Profile updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !idNumber.trim()) return;
    
    try {
      setIsVerifying(true);
      setError('');
      
      // Simulate ID verification (in production, this would call a verification API)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      await userService.verifyUser(currentUser.uid);
      
      // Refresh both profiles
      await Promise.all([
        loadProfile(true),
        refreshUserProfile()
      ]);
      
      setShowKYCForm(false);
      setIdNumber('');
      setMessage('Identity Verified Successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error verifying identity:', err);
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <Loader2 className="animate-spin text-emerald-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!firestoreProfile && !authUserProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400 mb-4">Unable to load profile</p>
          <button 
            onClick={handleRefresh}
            className="text-emerald-500 hover:text-emerald-400 font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const activeProfile = firestoreProfile || authUserProfile;
  const isLender = activeProfile?.roles?.includes('LENDER');
  const isEligibleForKYC = isLender ? totalCount >= KYC_THRESHOLD : true;
  const reportsNeeded = Math.max(0, KYC_THRESHOLD - totalCount);

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-6">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-slate-400 hover:text-white transition-colors group"
        >
          <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" /> 
          Back to Dashboard
        </button>
        
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Profile Source Indicator (for debugging/transparency) */}
      {!firestoreProfile && authUserProfile && (
        <div className="mb-6 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg text-blue-400 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          <span>Profile initialized from authentication data. Save changes to create your full profile.</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Profile Edit */}
        <div className="lg:col-span-2 bg-slate-800 rounded-2xl p-8 shadow-2xl border border-slate-700">
          <div className="flex items-center gap-3 mb-8 border-b border-slate-700 pb-6">
            <div className="bg-emerald-500/10 p-3 rounded-full">
              <User className="text-emerald-500 h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">My Profile</h1>
              <p className="text-slate-400">Manage your account details and roles</p>
            </div>
          </div>

          {/* Messages */}
          {message && (
            <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 animate-in slide-in-from-top-2 flex items-center gap-2">
              <CheckSquare size={18} />
              {message}
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 animate-in slide-in-from-top-2 flex items-center gap-2">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
              <input 
                type="text" 
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                placeholder="John Doe"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Mail size={16} className="text-slate-500" />
                Email Address
              </label>
              <input 
                type="email" 
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                placeholder="john@example.com"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Phone size={16} className="text-slate-500" />
                Mobile Number
              </label>
              <input 
                type="tel" 
                name="mobile"
                required
                value={formData.mobile}
                onChange={handleChange}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                placeholder="+1 (555) 000-0000"
              />
              <p className="text-xs text-slate-500 mt-2">
                Reports filed against you are linked to this number.
              </p>
            </div>

            {/* Roles */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-slate-300 mb-2">Account Roles</label>
              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={() => toggleRole('LENDER')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-bold transition-all ${
                    selectedRoles.includes('LENDER') 
                      ? 'bg-emerald-900/30 border-emerald-500 text-emerald-400' 
                      : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  {selectedRoles.includes('LENDER') ? <CheckSquare size={16}/> : <Square size={16}/>}
                  Lender
                </button>
                <button 
                  type="button"
                  onClick={() => toggleRole('BORROWER')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-bold transition-all ${
                    selectedRoles.includes('BORROWER') 
                      ? 'bg-blue-900/30 border-blue-500 text-blue-400' 
                      : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  {selectedRoles.includes('BORROWER') ? <CheckSquare size={16}/> : <Square size={16}/>}
                  Borrower
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Enable 'Lender' to submit reports. Enable 'Borrower' to manage claims against you.
              </p>
            </div>
            
            {/* Submit Button */}
            <div className="pt-4">
              <button 
                type="submit" 
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-lg shadow-lg shadow-emerald-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Right Column: Verification & Stats */}
        <div className="space-y-6">
          
          {/* Account Status Card */}
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 shadow-xl">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <ShieldCheck className={activeProfile?.isVerified ? "text-emerald-500" : "text-slate-500"} />
              Account Verification
            </h3>
            
            {activeProfile?.isVerified ? (
              <div className="bg-emerald-900/20 border border-emerald-500/30 p-4 rounded-lg">
                <p className="text-emerald-400 font-bold flex items-center gap-2">
                  <BadgeCheck size={18} /> Verified Account
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  Your identity has been verified. 
                  {isLender ? ' You have unlimited access to post reports.' : ' Your responses carry a verified badge.'}
                </p>
                {activeProfile.verificationDate && (
                  <p className="text-xs text-slate-500 mt-2">
                    Verified on: {
                      activeProfile.verificationDate.seconds 
                        ? new Date(activeProfile.verificationDate.seconds * 1000).toLocaleDateString()
                        : new Date(activeProfile.verificationDate).toLocaleDateString()
                    }
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg">
                <p className="text-slate-300 font-bold flex items-center gap-2">
                  <AlertCircle size={18} className="text-orange-400"/> Unverified
                </p>
                
                {isLender && (
                  <p className="text-xs text-slate-500 mt-2 mb-4">
                    New lenders are limited to {MAX_DAILY_POSTS} reports per day.
                  </p>
                )}
                
                {!showKYCForm ? (
                  <div className="space-y-3">
                    {isLender && (
                      <div>
                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                          <span>KYC Eligibility</span>
                          <span>{totalCount} / {KYC_THRESHOLD} Reports</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full transition-all" 
                            style={{ width: `${Math.min(100, (totalCount / KYC_THRESHOLD) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {isEligibleForKYC ? (
                      <button 
                        onClick={() => setShowKYCForm(true)}
                        className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold py-2 rounded transition-colors"
                      >
                        <Unlock size={16} /> Verify Identity
                      </button>
                    ) : (
                      <button 
                        disabled 
                        className="w-full bg-slate-700 text-slate-500 text-sm font-bold py-2 rounded cursor-not-allowed"
                      >
                        Submit {reportsNeeded} more report{reportsNeeded !== 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleKYCSubmit} className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-4">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">National ID / Passport</label>
                      <input 
                        type="text" 
                        required 
                        value={idNumber}
                        onChange={(e) => setIdNumber(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white focus:ring-1 focus:ring-emerald-500 outline-none"
                        placeholder="Enter ID Number"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button 
                        type="button"
                        onClick={() => {
                          setShowKYCForm(false);
                          setIdNumber('');
                        }}
                        className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs py-2 rounded transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        disabled={isVerifying || !idNumber}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold py-2 rounded disabled:opacity-50 transition-colors flex items-center justify-center gap-1"
                      >
                        {isVerifying ? (
                          <>
                            <Loader2 className="animate-spin" size={12} />
                            Verifying...
                          </>
                        ) : (
                          'Submit'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Usage Stats Card (Lender Only) */}
          {isLender && (
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="text-blue-400" />
                Usage Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-sm text-slate-400">Daily Posts</span>
                  <div className="text-right">
                    <span className={`font-mono font-bold ${
                      dailyCount >= MAX_DAILY_POSTS && !activeProfile?.isVerified 
                        ? 'text-red-400' 
                        : 'text-white'
                    }`}>
                      {dailyCount} {activeProfile?.isVerified ? '' : `/ ${MAX_DAILY_POSTS}`}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-900/50 rounded-lg">
                  <span className="text-sm text-slate-400">Total Lifetime Reports</span>
                  <span className="font-mono font-bold text-white">{totalCount}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;